import type { GameObject, ItemRecipe, Shield, Weapon } from '../types';
import { SkillType } from '../model/skills';
import { dmg } from '../model/game';
import { forgeRecipe } from '../model/recipe';

const shields: Shield[] = [9,8,7,6,5,4,3,2,1].map(sid => (
  {
    type: 'shield', slot: 'secondary',
    id: `Shield_0${sid}_FA`,
    emoji: '🛡️',
    tier: 2,
    stack: 1,
    maxLvl: 3,
    weight: 2,
    moveSpeed: -0.05,
    block: [20, 6],
    parryForce: 30,
    parryBonus: 1.5,
    skill: SkillType.Blocking,
    durability: [200, 50],
  }
));

const weapons: Weapon[] = [
  ...[1,2,3,4,5,6,7].map<Weapon>(id => ({
    type: 'weapon', slot: 'primary',
    id: `Axe_1H_0${id}_FA`,
    emoji: '🪓',
    tier: 2,
    stack: 1,
    maxLvl: 4,
    weight: 2,
    moveSpeed: -0.05,
    block: 12,
    parryForce: [20, 5],
    parryBonus: 2,
    skill: SkillType.Axes,
    toolTier: 1,
    damage: [
      dmg({ slash: 16, chop: 16 }),
      dmg({ slash: 16, chop: 16 }),
    ],
    knockback: 60,
    backstab: 3,
    attacks: [{
      type: 'melee',
      animation: 'swing_axe',
      chain: 3,
      chainCombo: 2,
      stamina: 14,
      walkSpeed: 0.2,
      rotationSpeed: 0.3,
      startNoise: 10,
      hitNoise: 40,
      range: 2.2,
    }],
    durability: [100, 50],
  })),
  ...[1,2,3,4,5].map<Weapon>(id => ({
    type: 'weapon', slot: 'both',
    id: `Axe2H_0${id}_FA`,
    emoji: '🪓',
    tier: 3,
    stack: 1,
    maxLvl: 4,
    weight: 2.5,
    moveSpeed: -0.2,
    block: 28,
    parryForce: [70, 5],
    parryBonus: 2,
    skill: SkillType.Axes,
    toolTier: 3,
    damage: [
      dmg({ slash: 16, chop: 16 }),
      dmg({ slash: 16, chop: 16 }),
    ],
    knockback: 70,
    backstab: 3,
    attacks: [{
      type: 'melee',
      animation: 'battleaxe_attack',
      chain: 3,
      chainCombo: 2,
      stamina: 16,
      walkSpeed: 0.1,
      rotationSpeed: 0.3,
      startNoise: 20,
      hitNoise: 30,
      range: 2.5,
      mul: { damage: 1, force: 1, stagger: 1.5 },
    }, {
      type: 'melee',
      animation: 'battleaxe_secondary',
      chain: 0,
      chainCombo: 2,
      stamina: 8,
      walkSpeed: 0.1,
      rotationSpeed: 0.1,
      startNoise: 10,
      hitNoise: 30,
      range: 2.5,
      mul: { damage: 0.5, force: 2, stagger: 4 },
    }],
    durability: [200, 50],
  })),
  ...[1,2,3].map<Weapon>(id => ({
    type: 'weapon', slot: 'both',
    id: `Hammer_2H_0${id}_FA`,
    emoji: '🔨',
    tier: 2,
    floating: true,
    stack: 1,
    maxLvl: 4,
    weight: 2,
    moveSpeed: -0.05,
    block: 12,
    parryForce: [30, 5],
    parryBonus: 2,
    skill: SkillType.Clubs,
    damage: [dmg({ blunt: 16 }), dmg({ blunt: 16 })],
    knockback: 80,
    backstab: 3,
    attacks: [{
      type: 'melee',
      animation: 'swing_longsword',
      chain: 3,
      chainCombo: 2,
      stamina: 8,
      walkSpeed: 0.3,
      rotationSpeed: 0.3,
      startNoise: 10,
      hitNoise: 40,
      range: 2.4,
    }, {
      type: 'melee',
      animation: 'mace_secondary',
      chain: 0,
      chainCombo: 2,
      stamina: 16,
      walkSpeed: 0.2,
      rotationSpeed: 0,
      startNoise: 10,
      hitNoise: 30,
      range: 2.5,
      mul: { damage: 2.5, force: 2, stagger: 2 },
    }],
    durability: [200, 50],
  })),
  ...[1,2,3,4,5,6].map<Weapon>(id => ({
    type: 'weapon', slot: 'both',
    id: `Staff_2H_0${id}_FA`,
    emoji: '🥍',
    tier: 2,
    stack: 1,
    maxLvl: 4,
    weight: 2.5,
    moveSpeed: -0.05,
    block: 16,
    parryForce: [40, 5],
    parryBonus: 2,
    skill: SkillType.Polearms,
    damage: [dmg({ blunt: 16 }), dmg({ blunt: 16 })],
    knockback: 30,
    backstab: 3,
    attacks: [{
      type: 'melee',
      animation: 'atgeir_attack',
      chain: 3,
      chainCombo: 2,
      stamina: 12,
      walkSpeed: 0.2,
      rotationSpeed: 0.5,
      startNoise: 20,
      hitNoise: 30,
      range: 3.2,
    }, {
      type: 'melee',
      animation: 'atgeir_secondary',
      chain: 0,
      chainCombo: 2,
      stamina: 24,
      walkSpeed: 0.2,
      rotationSpeed: 0.2,
      startNoise: 10,
      hitNoise: 30,
      range: 3,
      mul: { damage: 1, force: 10, stagger: 6 },
    }],
    durability: [125, 50],
  })),
  ...[1,2,3,4,5].map<Weapon>(id => ({
    type: 'weapon', slot: 'primary',
    id: `Sword_1H_0${id}_FA`,
    emoji: '🗡️',
    tier: 2,
    stack: 1,
    maxLvl: 4,
    weight: 0.8,
    moveSpeed: -0.05,
    block: 12,
    parryForce: [20, 5],
    parryBonus: 2,
    skill: SkillType.Swords,
    damage: [dmg({ slash: 16 }), dmg({ slash: 16 })],
    knockback: 40,
    backstab: 3,
    attacks: [{
      type: 'melee',
      animation: 'swing_longsword',
      chain: 3,
      chainCombo: 2,
      stamina: 8,
      walkSpeed: 0.3,
      rotationSpeed: 0.3,
      startNoise: 10,
      hitNoise: 40,
      range: 2.4,
    }, {
      type: 'melee',
      animation: 'sword_secondary',
      chain: 0,
      chainCombo: 2,
      stamina: 16,
      walkSpeed: 0.2,
      rotationSpeed: 0,
      startNoise: 10,
      hitNoise: 30,
      range: 2.4,
      mul: { damage: 3, force: 1, stagger: 1 },
    }],
    durability: [200, 50],
  })),
  ...[1,2,3,4,5,6].map<Weapon>(id => ({
    type: 'weapon', slot: 'both',
    id: `Sword_2H_0${id}_FA`,
    emoji: '🗡️',
    tier: 2,
    stack: 1,
    maxLvl: 4,
    weight: 0.8,
    moveSpeed: -0.05,
    block: 12,
    parryForce: [20, 5],
    parryBonus: 2,
    skill: SkillType.Swords,
    damage: [dmg({ slash: 18 }), dmg({ slash: 18 })],
    knockback: 40,
    backstab: 3,
    attacks: [{
      type: 'melee',
      animation: 'swing_longsword',
      chain: 3,
      chainCombo: 2,
      stamina: 8,
      walkSpeed: 0.3,
      rotationSpeed: 0.3,
      startNoise: 10,
      hitNoise: 40,
      range: 2.4,
    }, {
      type: 'melee',
      animation: 'sword_secondary',
      chain: 0,
      chainCombo: 2,
      stamina: 16,
      walkSpeed: 0.2,
      rotationSpeed: 0,
      startNoise: 10,
      hitNoise: 30,
      range: 2.4,
      mul: { damage: 3, force: 1, stagger: 1 },
    }],
    durability: [200, 50],
  })),
];

export const data: GameObject[] = [
  ...shields,
  ...weapons,
].map(item => ({ ...item, mod: 'Fantasy_Armoury' }) as GameObject)

export const recipes: ItemRecipe[] = [
  ...shields.map(({ id }) => forgeRecipe(1, { Bronze: 10, Wood: 2 }, { Bronze: 3, Wood: 3, DeerHide: 3, Iron: 3 }, id)),
  ...weapons.map(({ id }) => forgeRecipe(1, { Bronze: 10, Wood: 2 }, { Bronze: 3, Wood: 3, DeerHide: 3, Iron: 3 }, id)),
];
